import type { ReactNode } from 'react';
import { useCompanyStore } from '../stores/company';
import type { FeatureKey } from './types';

/**
 * Progressive disclosure (PRD §5.11). A company with a feature turned off
 * must show NO affordance for it anywhere — not a disabled button, nothing.
 * This is the mechanism that lets a two-person cloth trader and a job-work
 * manufacturing unit share one codebase without either seeing the other's
 * fields.
 */

export function useFeature(flag: FeatureKey): boolean {
  const features = useCompanyStore((s) => s.features);
  return Boolean(features?.flags[flag]);
}

export function useFeatures() {
  return useCompanyStore((s) => s.features);
}

export interface FeatureProps {
  flag: FeatureKey;
  /** Render this instead when the flag is off. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

/** Conditionally renders children when the active company has `flag` turned on. */
export function Feature({ flag, fallback = null, children }: FeatureProps) {
  const on = useFeature(flag);
  return <>{on ? children : fallback}</>;
}

/** Human labels + descriptions for the F11-style features screen. */
export const FEATURE_META: Record<FeatureKey, { label: string; help: string; requires?: FeatureKey[] }> = {
  maintain_inventory: {
    label: 'Maintain inventory',
    help: 'Off: accounts-only mode, no item fields anywhere (services businesses).',
  },
  multi_godown: {
    label: 'Multiple godowns / locations',
    help: 'Off: single implicit location, no godown column.',
    requires: ['maintain_inventory'],
  },
  batches_and_expiry: {
    label: 'Batches & expiry',
    help: 'Off: no batch fields.',
    requires: ['maintain_inventory'],
  },
  serial_tracking: {
    label: 'Serial number tracking',
    help: 'Off: no serial capture.',
    requires: ['maintain_inventory'],
  },
  bill_wise_details: {
    label: 'Bill-wise details',
    help: 'Off: party balances only, no ageing.',
  },
  cost_centres: {
    label: 'Cost centres',
    help: 'Off: no allocation step.',
  },
  budgets: {
    label: 'Budgets',
    help: 'Off: hidden entirely.',
  },
  bom_manufacturing: {
    label: 'Bill of materials / manufacturing',
    help: 'Off: no conversion vouchers.',
    requires: ['maintain_inventory'],
  },
  job_work: {
    label: 'Job work',
    help: 'Off: no Material In / Material Out.',
    requires: ['maintain_inventory'],
  },
  order_processing: {
    label: 'Order processing',
    help: 'Off: no purchase/sales order references.',
  },
  actual_vs_billed_qty: {
    label: 'Actual vs billed quantity',
    help: 'Off: single quantity column.',
    requires: ['maintain_inventory'],
  },
  multiple_price_levels: {
    label: 'Multiple price levels',
    help: 'Off: single rate.',
  },
  interest_calculation: {
    label: 'Interest calculation',
    help: 'Off: hidden entirely.',
  },
  discount_columns: {
    label: 'Discount columns',
    help: 'Off: no discount fields on voucher lines.',
  },
  additional_charges: {
    label: 'Additional charges',
    help: 'Off: no charges section (freight, packing, etc.).',
  },
  gst_enabled: {
    label: 'GST',
    help: 'Off: non-GST mode, for businesses below the registration threshold.',
  },
};

/** Toggle combinations that PRD/api-phase0 require to depend on maintainInventory. */
export const INVENTORY_DEPENDENT_FEATURES: FeatureKey[] = [
  'multi_godown',
  'batches_and_expiry',
  'serial_tracking',
  'bom_manufacturing',
  'job_work',
  'actual_vs_billed_qty',
];
